import type { ActiveReportDetail, ActiveReportProductionSite } from '@/api/schemas/reports/common'
import type {
    GetAdditionalActionProcessPayloadSchema,
    GetAdditionalActionReportProcessCodePayloadSchema
} from '@/api/schemas/reports/operatorReporting'
import { REPORTS } from '@/constants'

export type ActiveReportAdditionalActionsRequest =
    | {
          kind: 'process'
          payload: GetAdditionalActionProcessPayloadSchema
      }
    | {
          kind: 'process-code'
          payload: GetAdditionalActionReportProcessCodePayloadSchema
      }

type RuntimeStatusSource = Pick<ActiveReportDetail, 'objectType' | 'statusId'>

export function isActiveProcessReport(report: Pick<ActiveReportDetail, 'objectType'> | null | undefined): boolean {
    return Number(report?.objectType) === REPORTS.REPORT_OBJECT_TYPES.OTHER_TASK
}

export function isActiveReportFinished(report: Pick<RuntimeStatusSource, 'statusId'> | null | undefined): boolean {
    return Number(report?.statusId) === REPORTS.REPORT_STATUSES.FINISHED
}

export function isActiveReportStopped(report: Pick<RuntimeStatusSource, 'statusId'> | null | undefined): boolean {
    return Number(report?.statusId) === REPORTS.REPORT_STATUSES.STOPPED
}

export function isOffsetPrintReport(
    report: Pick<ActiveReportDetail, 'printVariant' | 'productionSite'> | null | undefined
): boolean {
    const productionSiteTypeId = report?.productionSite?.typeId

    if (productionSiteTypeId !== null && productionSiteTypeId !== undefined) {
        return Number(productionSiteTypeId) === REPORTS.WORKSPACE_TYPES.OFFSET_PRINT
    }

    return report?.printVariant === REPORTS.PRINT_VARIANTS.OFFSET
}

export function allowsReportExcess(productionSite: ActiveReportProductionSite | null | undefined): boolean {
    return productionSite?.allowsReportExcessAmount === true || productionSite?.reportOverMaxAmount === true
}

export function shouldShowControlCodeSection(
    report: Pick<ActiveReportDetail, 'objectType' | 'objectDetail'> | null | undefined
): boolean {
    if (!report) {
        return false
    }

    if (Number(report.objectType) === REPORTS.REPORT_OBJECT_TYPES.ORDER) {
        return true
    }

    const reportItemType = Number(report.objectDetail?.reportItemType)

    return (
        reportItemType === REPORTS.REPORT_ITEMS_TYPES.PRODUCTION_SCHEDULE ||
        reportItemType === REPORTS.REPORT_ITEMS_TYPES.FACTORY_BASE_JOB
    )
}

export function shouldShowAdditionalBoardsAction(
    report: Pick<ActiveReportDetail, 'objectType' | 'printVariant' | 'productionSite'> | null | undefined
): boolean {
    return Boolean(report && !isActiveProcessReport(report) && isOffsetPrintReport(report))
}

export function getRuntimeReportAmount(
    report: Pick<ActiveReportDetail, 'leftAmount' | 'reportAmount' | 'statusId'> | null
): number | null {
    if (!report) {
        return null
    }

    if (isActiveReportFinished(report) || isActiveReportStopped(report)) {
        return report.reportAmount ?? null
    }

    return report.leftAmount ?? report.reportAmount ?? null
}

export function buildRuntimeAdditionalActionsRequest(
    report: ActiveReportDetail | null
): ActiveReportAdditionalActionsRequest | null {
    if (!report?.id || !report.productionSiteId) {
        return null
    }

    const queryString = `?reportId=${report.id}`

    if (isActiveProcessReport(report)) {
        if (!report.processCodeId) {
            return null
        }

        return {
            kind: 'process-code',
            payload: {
                processCodeId: report.processCodeId,
                productionSiteId: report.productionSiteId,
                queryString
            }
        }
    }

    const processId = report.productionProcessId ?? report.process

    if (!processId) {
        return null
    }

    return {
        kind: 'process',
        payload: {
            processId,
            productionSiteId: report.productionSiteId,
            queryString
        }
    }
}
